<script lang="ts" setup>
import { onMounted, onUnmounted, ref } from 'vue';
import { MENU, RouteRecordNormalized } from 'vue-router';

import NavbarMenu from '@/components/layout/NavbarMenu.vue';
import { container } from '@/container';
import { Router } from '@/services/router/Router';
import { INavbarItem } from '~/INavbarItem';

interface Props {
  dataTestid?: string;
  menu: MENU;
  routes?: RouteRecordNormalized[];
  level?: number;
}

const props = withDefaults(defineProps<Props>(), {
  dataTestid: 'navbar',
  routes: undefined,
  level: 0,
});

const router = container.get(Router);

const routes = (props.routes ?? router.getMenuRoutes(props.menu)).map((route) => <INavbarItem> {
  open: false,
  route,
});

// Refs.
const menu = ref<InstanceType<typeof NavbarMenu>>();
const items = ref(routes);

// Methods.
const toggleMenu = (activeItem?: INavbarItem) => {
  items.value.forEach((item) => {
    if (activeItem?.route.name === item.route.name) {
      item.open = !!(item.route.children?.length && !activeItem?.open);
    }
    else {
      item.open = false;
    }
  });
};

const clickHandler = (event: Event) => {
  const { target } = <Event & { target: HTMLElement }> event;
  if (menu.value?.$el.contains(target) || (typeof target.className === 'string' && target.className?.includes('navbar-menu-item--link'))) {
    return;
  }
  toggleMenu();
};

onMounted(() => {
  document.addEventListener('click', clickHandler);
});

onUnmounted(() => {
  document.removeEventListener('click', clickHandler);
});

defineExpose({ menu });
</script>

<template>
  <nav
    class="navbar"
    :class="`${props.dataTestid}--${props.menu}`"
    :data-testid="`${props.dataTestid}_${props.menu}`"
  >
    <slot name="before" />

    <NavbarMenu
      ref="menu"
      :data-testid="`${props.dataTestid}_menu`"
      :items="items"
      @click="toggleMenu"
    />

    <slot name="after" />
  </nav>
</template>

<style lang="scss">
  .navbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;

    &--more-things {
      display: flex;
      align-items: center;
    }
  }
</style>
